import { CustomerAddress, ShopItem as ShopItemType } from '@@types'
import classNames from 'classnames'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactSVG } from 'react-svg'
import { useMediaQuery } from 'usehooks-ts'

import { useAddressStore } from '@entities/Address'

import { useModalStore } from '@features/modal'

import { useSlots } from '@widgets/slots'

import useShopTime from '@shared/hooks/useShopTime'
import Button from '@shared/ui/button'
import Radio from '@shared/ui/radio'

import styles from './PickupView.module.scss'



interface ShopItemProps {
  shop: ShopItemType;
  handleShopClick: (shop: ShopItemType) => void;
  handleDeleteAddress: (shop: ShopItemType) => void;
  isSearchMode: boolean;
  selected?: boolean;
  onSelect?: () => void;
}

const ShopItem = ({
  shop,
  handleShopClick,
  handleDeleteAddress,
  isSearchMode,
  selected,
  onSelect,
}: ShopItemProps) => {
  const { t: tc } = useTranslation('common')
  const { open, close, getShopStatus } = useShopTime(
    shop?.open_time || 0,
    shop?.close_time || 0,
  )

  const handleClick = () => {
    if (isSearchMode) {
      onSelect?.()
    }
    else {
      handleShopClick(shop)
    }
  }

  return (
    <li
      onClick={handleClick}
      className={classNames(styles.address, {
        [styles['address-initial']]: isSearchMode,
      })}
    >
      {isSearchMode && <Radio radioSize="large" checked={selected} />}
      <div className={styles.address__content}>
        <span className={styles.address__name}>{shop?.address}</span>
        {!isSearchMode && (
          <div className={styles.address__time}>
            <span>{getShopStatus ? tc('shop.open') : tc('shop.closed')}</span>
            <span>
              {open} {tc('from')} {close}
            </span>
          </div>
        )}
      </div>
      <div
        className={classNames(styles.address__right, styles.address__delete)}
      >
        {isSearchMode && (
          <ReactSVG
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              handleDeleteAddress(shop)
            }}
            src="/icons/delete.svg"
            wrapper="span"
          />
        )}
      </div>
    </li>
  )
}

interface ShopListProps {
  shops: (ShopItemType | null)[] | null;
  isSearchMode: boolean;
  handleShopClick: (shop: ShopItemType) => void;
  handleDeleteAddress: (shop: ShopItemType) => void;
}
export const ShopList = ({
  shops,
  isSearchMode,
  handleShopClick,
  handleDeleteAddress,
}: ShopListProps) => {
  const [ pendingShop, setPendingShop ] = useState<ShopItemType | null>(null)
  const { selectedAddress, setSelectedAddress, setDeliveryType } =
    useAddressStore()
  const { closeModal } = useModalStore()
  const { openSlotsModal } = useSlots()
  const isTablet = useMediaQuery('(max-width: 991px)')
  const { t: tc } = useTranslation('common')

  const handleSubmit = () => {
    if (pendingShop) {
      setSelectedAddress({
        shop_id: pendingShop.shop_id,
        address: pendingShop.address,
        customerGeoPoint: {
          latitude: pendingShop.shop?.shopGeoPoint?.latitude ?? 0,
          longitude: pendingShop.shop?.shopGeoPoint?.longitude ?? 0        },
      } as CustomerAddress)
      setDeliveryType('pickup')
      closeModal('delivery')
      openSlotsModal()
    }
  }

  const currentSelectedShopId =
    pendingShop?.shop_id || selectedAddress?.shop_id

  return (
    <>
      <ul className={styles.addressList}>
        {shops?.map((shop) => {
          if (!shop) return null

          const isSelected = currentSelectedShopId === shop.shop_id

          return (
            <ShopItem
              key={shop.shop_id}
              shop={shop}
              isSearchMode={isSearchMode}
              handleDeleteAddress={handleDeleteAddress}
              handleShopClick={handleShopClick}
              selected={isSelected}
              onSelect={() => setPendingShop(shop)}
            />
          )
        })}
      </ul>
      {isSearchMode && shops?.length ? (
        <Button
          disabled={
            !pendingShop || pendingShop.shop_id === selectedAddress?.shop_id
          }
          type="button"
          size={!isTablet ? 'large' : 'medium'}
          onClick={handleSubmit}
        >
          {tc('choose')}
        </Button>
      ) : null}
    </>
  )
}
